import { Injectable } from '@angular/core';
import { DxListsStorage } from '@core/storage/dx-lists.storage';
import { DcDxGrids } from '@core/utils/dx-types';
import { ProductInstanceModel } from '@dc-console-api';
import { randUuid } from '@ngneat/falso';
import { IFeatureModel } from '@requesters/models/feature.model';
import { IOrganizationModel } from '@requesters/models/organization.model';
import { SupportedLanguages } from '@requesters/models/preferences.model';
import { IAccountModel, IProfileModel } from '@requesters/models/profile.model';
import { Map } from 'immutable';

import { CurrentUser } from '../domain/current-user';
import { Features } from '../domain/features';
import { InstanceModule } from '../domain/instance-module';
import {
	AvailableModulesMapping,
	ModuleRole,
	RolesMapping,
} from '../domain/module-role';
import { Organization } from '../domain/organization';
import { LanguagePreference } from '../domain/preferences';

@Injectable({
	providedIn: 'root',
})
export class AppAdapter {
	public readonly languageMapping: { [k: string]: LanguagePreference } = {
		[SupportedLanguages.BROWSER]: LanguagePreference.Browser,
		[SupportedLanguages.FR]: LanguagePreference.Fr,
		[SupportedLanguages.EN]: LanguagePreference.En,
	};

	public constructor(private readonly dxListsStorage: DxListsStorage) {}

	public generateCurrentUser(response: IProfileModel): CurrentUser {
		const tuple = response.accounts.map<[number, Array<ModuleRole>]>((a) => [
			a.organizationId,
			this.generateRoles(a),
		]);

		return CurrentUser.build({
			id: response.id,
			login: response.login ?? '',
			userName:
				response.firstname && response.lastname
					? `${response.firstname} ${response.lastname}`
					: '',
			primaryOrganizationId: response.organization.id,
			accounts: Map(tuple),
		});
	}

	public generateAvailableInstanceModules(
		availableInstanceModules: ReadonlyArray<ProductInstanceModel>,
		location: Location,
	): Map<string, InstanceModule> {
		const instances = availableInstanceModules
			.filter((inst) => !location.origin.includes(inst.publicUrl))
			.map<[string, InstanceModule]>((item) => {
				const id = randUuid();
				return [
					id,
					InstanceModule.build({
						id,
						title: item.label ?? 'Titre non indiqué',
						type: AvailableModulesMapping[item.type] ?? null,
						publicUrl: item.publicUrl,
					}),
				];
			});

		return Map(instances);
	}

	public generateOrganizationPerUserAccount(
		orgPerAccount: Array<IOrganizationModel>,
	): Map<number, Organization> {
		const tuple = orgPerAccount.map<[number, Organization]>((o) => [
			o.id,
			Organization.build({
				id: o.id,
				code: o.code,
				name: o.name,
			}),
		]);
		return Map(tuple);
	}

	public generateFeatures(features: IFeatureModel): Features {
		return Features.build({
			canResetUserPassword: features.usersResetPasswordManagement,
			canManageUsers: features.usersManagement,
			canManageUserLogin: features.usersLoginManagement,
			canManageUserRoles: features.usersRolesManagement,
			canManageMyTokens: features.usersTokens,
			canManageUserTokens: features.usersTokensManagement,
			canManageGroups: features.groupsManagement,
			canManageGroupRoles: features.groupsRolesManagement,
			canManageInstances: features.instancesManagement,
			enabledInstanceTypes: features.instancesTypeEnabled
				? features.instancesTypeEnabled.map(
						(instanceType) => AvailableModulesMapping[instanceType],
					)
				: [],
			canManageOrganizations: features.organizationsManagement,
		});
	}

	public updateStoredGridsConfig(feature: Features): void {
		if (!feature.canManageUsers) {
			this.dxListsStorage.sanitize(DcDxGrids.UserListStandalone);
			this.dxListsStorage.sanitize(DcDxGrids.UserDetailsAffiliatedGroups);
		}
		if (!feature.canManageGroups) {
			this.dxListsStorage.sanitize(DcDxGrids.GroupListStandalone);
			this.dxListsStorage.sanitize(DcDxGrids.GroupDetailsAffiliatedUsers);
		}
	}

	private generateRoles(account: IAccountModel): Array<ModuleRole> {
		let directRoles = account.userRoles.map((role) => RolesMapping[role]);
		if (!account.groups) {
			return directRoles;
		}
		if (account.groups) {
			directRoles = account.groups.reduce((acc, accountGroup) => {
				accountGroup.roles.forEach((r) => acc.push(RolesMapping[r]));
				return acc;
			}, directRoles);
		}
		return directRoles;
	}
}
